import React from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  Users,
  Sparkles,
  Zap,
  ShieldCheck,
  Crown,
  ArrowUpRight,
  Rocket,
} from "lucide-react";
import { Navigation } from "../components/sections/Navigation";
import { Footer } from "../components/sections/Footer";

const featureBlocks = [
  {
    icon: Users,
    title: "Build Your Squad",
    body: "Pick 15 players from across the 18 SPL clubs — 2 goalkeepers, 5 defenders, 5 midfielders, 3 forwards — inside a fixed budget.",
  },
  {
    icon: Crown,
    title: "Captain & Vice-Captain",
    body: "Nominate the star performer of your matchday. Your captain scores double. Get it right and you rocket up the leaderboard.",
  },
  {
    icon: Zap,
    title: "Weekly Scoring",
    body: "Goals, assists, clean sheets, saves and bonus points — every matchday your XI earns points based on real SPL performance.",
  },
  {
    icon: ShieldCheck,
    title: "Free Transfers",
    body: "One free transfer between matchdays. Use it wisely — extra changes cost points, chips let you break the rules once per season.",
  },
  {
    icon: Trophy,
    title: "Private & Public Leagues",
    body: "Set up a league for your friends, your office, your matatu SACCO or your neighbourhood. Track head-to-head glory all season.",
  },
  {
    icon: Sparkles,
    title: "Chips & Boosts",
    body: "Triple Captain, Bench Boost, Free Hit, Wildcard — game-changing tools that transform a decent gameweek into a triumphant one.",
  },
];

const howItWorksSteps = [
  {
    number: "01",
    title: "Register your team",
    body: "Sign up in under a minute, name your fantasy club and get your KPL Fantasy Manager ID.",
  },
  {
    number: "02",
    title: "Draft your squad",
    body: "Spend your budget across 15 SPL players. Balance stars, form picks and hidden gems.",
  },
  {
    number: "03",
    title: "Set your starting XI",
    body: "Each matchday, pick 11 to play and 4 to bench. Choose your captain and vice-captain.",
  },
  {
    number: "04",
    title: "Score, transfer, climb",
    body: "Live scoring during matchdays. Use transfers and chips to stay ahead of your league.",
  },
];

const HeroBadge = ({ children, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-2 px-4 py-2 border border-[#D1111A]/30 bg-[#D1111A]/5 text-[10px] font-bold uppercase tracking-[0.28em] text-[#D1111A]"
  >
    {children}
  </span>
);

export const FantasyPage = () => {
  return (
    <div className="App bg-[#FAFAFA] min-h-screen" data-testid="fantasy-page">
      <Navigation />

      {/* Hero */}
      <section
        className="pt-32 pb-16 md:pb-24 px-6 bg-white border-b border-[#E4E4E7] relative overflow-hidden"
        data-testid="fantasy-hero"
      >
        <div className="max-w-6xl mx-auto relative">
          <HeroBadge testId="fantasy-hero-badge">
            <Sparkles size={12} />
            <span>Fantasy League · Launching Soon</span>
          </HeroBadge>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 font-bold text-4xl sm:text-6xl lg:text-7xl text-[#0A0A0A] leading-[1.02]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="fantasy-title"
          >
            Fantasy
            <br />
            <span className="text-[#D1111A] italic">Kenya Premier League</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 max-w-2xl text-base sm:text-lg text-[#0A0A0A]/70 leading-relaxed"
            data-testid="fantasy-intro"
          >
            The official fantasy football game of the Kenya Premier League. Pick your dream XI
            from every club, captain the star of the matchday, run leagues with your friends —
            and prove you can pick a winner better than anyone in the country.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.22em] font-bold opacity-60 cursor-not-allowed"
              data-testid="fantasy-signup-button"
              title="Fantasy league opens soon"
            >
              <Rocket size={14} />
              Register — Coming Soon
            </button>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-[#0A0A0A] hover:text-[#D1111A] transition-colors"
              data-testid="fantasy-learn-more-link"
            >
              How it works
              <ArrowUpRight size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Feature grid */}
      <section
        className="py-16 md:py-24 px-6 bg-[#FAFAFA]"
        data-testid="fantasy-features-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#D1111A] mb-3">
              The Game
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] leading-tight max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="fantasy-features-heading"
            >
              Everything you love about fantasy football, built for the SPL.
            </h2>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="fantasy-features-grid"
          >
            {featureBlocks.map((f, idx) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(idx * 0.05, 0.25) }}
                className="group relative p-6 md:p-7 bg-white border border-[#E4E4E7] hover:border-[#D1111A] transition-colors"
                data-testid={`fantasy-feature-${idx}`}
              >
                <f.icon className="w-8 h-8 text-[#D1111A] mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">{f.title}</h3>
                <p className="text-sm text-[#0A0A0A]/70 leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="py-16 md:py-24 px-6 bg-[#0A0A0A] text-white relative overflow-hidden"
        data-testid="fantasy-how-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#D1111A] mb-3">
                How it works
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="fantasy-how-heading"
              >
                From <span className="italic text-white/70">picking your first XI</span> to lifting the KPL Fantasy Cup — in four steps.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/10">
            {howItWorksSteps.map((s, idx) => (
              <motion.div
                key={s.number}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="p-6 md:p-8 border-r border-b border-white/10 relative"
                data-testid={`fantasy-step-${idx}`}
              >
                <span
                  className="block font-bold text-4xl md:text-5xl text-[#D1111A] mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {s.number}
                </span>
                <h3 className="text-base font-bold mb-2 uppercase tracking-wide">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Countdown / launch panel */}
      <section
        className="py-16 md:py-24 px-6 bg-[#FAFAFA]"
        data-testid="fantasy-launch-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center border border-[#E4E4E7] bg-white p-8 md:p-12">
            <div className="lg:col-span-3">
              <HeroBadge>
                <Sparkles size={12} />
                <span>Season Launch</span>
              </HeroBadge>
              <h2
                className="mt-6 text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="fantasy-launch-heading"
              >
                Be the first Fantasy KPL Champion.
              </h2>
              <p className="mt-5 text-base text-[#0A0A0A]/70 leading-relaxed max-w-xl">
                We&apos;re finalising player valuations, squad rosters and the scoring system with
                the clubs. The game opens ahead of the next SPL matchday. Register your
                interest and we&apos;ll email you the moment squads unlock.
              </p>

              <form
                className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg"
                onSubmit={(e) => e.preventDefault()}
                data-testid="fantasy-register-form"
              >
                <input
                  type="email"
                  placeholder="your@email.com"
                  disabled
                  className="flex-1 px-4 py-3 border border-[#0A0A0A]/15 bg-[#F4F4F5] text-sm placeholder:text-[#0A0A0A]/40 disabled:cursor-not-allowed"
                  data-testid="fantasy-email-input"
                />
                <button
                  type="submit"
                  disabled
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D1111A] text-white text-xs uppercase tracking-[0.22em] font-bold opacity-60 cursor-not-allowed"
                  data-testid="fantasy-notify-button"
                  title="Email registration opens soon"
                >
                  Notify me
                </button>
              </form>
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A]/45">
                Registration opens closer to launch. No spam, one launch email.
              </p>
            </div>

            <div className="lg:col-span-2 border-l-0 lg:border-l border-t lg:border-t-0 border-[#E4E4E7] pt-8 lg:pt-0 lg:pl-10">
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#D1111A] mb-4">
                Prizes on the line
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Trophy className="w-4 h-4 text-[#D1111A] mt-1 shrink-0" />
                  <span className="text-sm text-[#0A0A0A]/75 leading-relaxed">
                    Season champion trophy — Fantasy KPL Cup, presented at the SPL awards.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Crown className="w-4 h-4 text-[#D1111A] mt-1 shrink-0" />
                  <span className="text-sm text-[#0A0A0A]/75 leading-relaxed">
                    Monthly Manager of the Month recognition on kenyapremierleague.com.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-4 h-4 text-[#D1111A] mt-1 shrink-0" />
                  <span className="text-sm text-[#0A0A0A]/75 leading-relaxed">
                    Weekly matchday winner shout-outs across KPL channels.
                  </span>
                </li>
              </ul>
              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-[#0A0A0A]/40">
                Prizes subject to sponsor confirmation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FantasyPage;
